/* External */
import { FormattedMessage, injectIntl } from 'react-intl';
import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';

/* Ott-common */
import Loading from '@dtvgo/loading';

/* Assets */
import logo from '@/assets/theme/svg/logo.svg';

/* Styles */
import './InsertPin.scss';

/* Actions */
import { setSelectedProfile } from '../state/profile/reducer';

/* Other */
import {
  IS_BRAZIL,
  PIN_LOGIN_URLS_BR,
  PIN_LOGIN_URLS,
  userGtmEventHandler,
  getAuthTokensData,
  checkRedirectProfile,
  PROFILES_STORAGE,
  PATHS,
  alcatrazFeatureFlag,
  getEncryptedData,
  setEncryptedData,
} from '../utils';
import { useGtmContext } from '../components/GtmContext';
import { gtmDimensions } from '../components/GtmContext/dimensions/tvValidation';
import { tvService, backendService } from '../services';
import { getImage } from '../theme/images';

/**
 * Constants
 */
export const SUCCESSFUL_RESULT = 'exitoso';
const FAILED_RESULT = 'fallido';
const pinLoginUrls = IS_BRAZIL ? PIN_LOGIN_URLS_BR : PIN_LOGIN_URLS;

function InsertPin({ intl: { formatMessage }, location: { search } }) {
  const { gtmUserData } = useGtmContext();
  const [pin, setPin] = useState('');
  const [redirect, setRedirect] = useState(null);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const profileStoraged = alcatrazFeatureFlag
    ? getEncryptedData(PROFILES_STORAGE.profile)
    : JSON.parse(localStorage.getItem(PROFILES_STORAGE.profile));

  const queryParams = new URLSearchParams(search);
  const pinFromQueryParams = queryParams.get('pinCode');
  const dispatch = useDispatch();

  useEffect(() => {
    userGtmEventHandler(gtmDimensions.web_enter_code.page_view, gtmUserData);
  }, [gtmUserData]);

  const activeTvToolbox = useCallback(async (pinCode) => {
    try {
      await tvService.v2.tv.activateDevice(
        pinCode,
        getAuthTokensData().idToken,
      );
      history.push(`${pinLoginUrls.main}/${SUCCESSFUL_RESULT}`);
    } catch {
      history.push(`${pinLoginUrls.main}/${FAILED_RESULT}`);
    }
  }, [history]);

  useEffect(() => {
    if (pinFromQueryParams) {
      activeTvToolbox(pinFromQueryParams);
    }
  }, [activeTvToolbox, pinFromQueryParams]);

  const confirmPin = async (e) => {
    if (e) e.preventDefault();
    activeTvToolbox(pin);
  };

  const handlePin = ({ target: { value } }) => {
    const pattern = /^[\da-z]+$/i;
    if (value === '' || pattern.test(value)) {
      setPin(value);
    }
  };

  function handleClick() {
    userGtmEventHandler(
      gtmDimensions.web_enter_code.enter_validation_code,
      gtmUserData,
    );
  }

  useEffect(() => {
    if (!profileStoraged) {
      setLoading(true);
      backendService.customer.v1.getProfiles().then((res) => {
        const mainProfile = res.data.find((profile) => profile.isMainProfile);
        const checkProfile = checkRedirectProfile(res.data);
        dispatch(setSelectedProfile(mainProfile));

        if (alcatrazFeatureFlag) {
          setEncryptedData(mainProfile, PROFILES_STORAGE.profile);
        } else {
          localStorage.setItem(PROFILES_STORAGE.profile, JSON.stringify(mainProfile ?? {}));
        }

        if (checkProfile) {
          setRedirect(checkProfile);
        }

        setLoading(false);

        return null;
      }).catch((error) => {
        history.push(PATHS?.ERROR, {
          source: 'MIDDLEWARE',
          location: 'LOGIN',
          type: 'UNEXPECTED',
          externCode: error?.response?.status,
        });
      });
    }
  }, [profileStoraged, history, dispatch]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (loading) {
    return (
      <div className="dtv-web-user-profile">
        <Loading className="dev-web-user-profile__loading" />
      </div>
    );
  }

  return (
    <form className="dtv-insert-pin" onSubmit={confirmPin}>
      <img src={getImage('logo') || logo} className="dtv-img-logo" alt="Logo" />
      <p className="dtv-insert-pin-text">
        <FormattedMessage id="pinLogin.insert" defaultMessage="Inserta el pin que figura en tu televisor" />
      </p>
      <div className="dtv-image-container" />
      <input
        type="text"
        className="dtv-input"
        onChange={handlePin}
        placeholder={formatMessage({
          id: 'pinLogin.activationPin',
          defaultMessage: 'Pin de activación',
        })}
        value={pin}
        maxLength="8"
      />
      <button type="submit" onClick={() => handleClick()} className="dtv-btn-done" disabled={!pin}>
        <FormattedMessage id="pinLogin.done" defaultMessage="¡HECHO!" />
      </button>
    </form>
  );
}

InsertPin.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

InsertPin.defaultProps = {};

export default injectIntl(InsertPin);
